import React, { createContext, useContext, useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Drawer,
  Group,
  Stack,
  Text,
} from '@mantine/core';
import type { ProjectDetail, WorkflowCitation } from '@tinypro-audit/contracts';
import { uniqueEvidence } from './project-review-model';

type Viewer = {
  data: ProjectDetail;
  open: (citation: WorkflowCitation) => void;
  assessment: (id: string) => void;
};
const EvidenceContext = createContext<Viewer | null>(null);

export const documentUrl = (
  id: string,
  page?: number | null,
  preview = false,
) =>
  `/api/projects/documents/${encodeURIComponent(id)}${preview ? '?preview=1' : ''}${page ? `#page=${page}&view=FitH` : ''}`;

export function ProjectEvidenceProvider({
  data,
  onAssessment,
  children,
}: {
  data: ProjectDetail;
  onAssessment: (id: string) => void;
  children: React.ReactNode;
}) {
  const [selected, setSelected] = useState<WorkflowCitation | null>(null);
  const doc = data.documents.find((d) => d.id === selected?.id);
  const filename = String(doc?.metadata.original_filename ?? 'Source document');
  const mime = String(doc?.metadata.mime_type ?? '');
  const previewable = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/webp',
  ].includes(mime);
  return (
    <EvidenceContext.Provider
      value={{ data, open: setSelected, assessment: onAssessment }}
    >
      {children}
      <Drawer
        opened={selected !== null}
        onClose={() => setSelected(null)}
        position="right"
        size="xl"
        title={filename}
        closeButtonProps={{ 'aria-label': 'Close document preview' }}
        styles={{
          title: { minWidth: 0, overflowWrap: 'anywhere' },
          close: { flexShrink: 0 },
        }}
      >
        {selected && (
          <Stack>
            <Group justify="space-between">
              <Text size="sm">
                {selected.page
                  ? `Cited page ${selected.page}`
                  : 'Original evidence'}
              </Text>
              <Button
                component="a"
                href={documentUrl(selected.id)}
                size="xs"
                variant="default"
              >
                Download original
              </Button>
            </Group>
            {selected.quote && (
              <Alert color="blue" title="Cited excerpt">
                <Text size="sm">“{selected.quote}”</Text>
              </Alert>
            )}
            {previewable ? (
              mime === 'application/pdf' ? (
                <>
                  <iframe
                    key={`${selected.id}:${selected.page}`}
                    src={documentUrl(selected.id, selected.page, true)}
                    title={`${filename}${selected.page ? `, page ${selected.page}` : ''}`}
                    className="project-document-frame"
                  />
                  <Anchor
                    href={documentUrl(selected.id, selected.page, true)}
                    target="_blank"
                    rel="noreferrer"
                    size="sm"
                  >
                    Open document in a separate tab
                  </Anchor>
                </>
              ) : (
                <img
                  className="project-document-image"
                  src={documentUrl(selected.id, null, true)}
                  alt={filename}
                />
              )
            ) : (
              <Text size="sm">
                Preview is unavailable for this file type. Download the original
                to inspect it.
              </Text>
            )}
          </Stack>
        )}
      </Drawer>
    </EvidenceContext.Provider>
  );
}

export function Evidence({
  items,
  quotes = true,
}: {
  items: WorkflowCitation[];
  quotes?: boolean;
}) {
  const viewer = useContext(EvidenceContext);
  return (
    <Stack gap="xs">
      {uniqueEvidence(items).map((e, i) => {
        const doc = viewer?.data.documents.find((d) => d.id === e.id);
        const filename = String(
          doc?.metadata.original_filename ?? 'Source document',
        );
        return (
          <div
            key={`${e.kind}:${e.id}:${e.page}:${i}`}
            className="project-source"
          >
            {e.kind === 'document' ? (
              <Anchor
                size="sm"
                href={documentUrl(e.id, e.page, true)}
                target="_blank"
                rel="noreferrer"
                onClick={
                  viewer
                    ? (event) => {
                        event.preventDefault();
                        viewer.open(e);
                      }
                    : undefined
                }
              >
                {filename}
                {e.page ? ` · page ${e.page}` : ''}
              </Anchor>
            ) : e.kind === 'measurement_assessment' ? (
              <Anchor
                size="sm"
                href={`#assessment-${e.id}`}
                onClick={
                  viewer
                    ? (event) => {
                        event.preventDefault();
                        viewer.assessment(e.id);
                      }
                    : undefined
                }
              >
                Original pre-site form review
              </Anchor>
            ) : e.kind === 'manufacturer' ? (
              <Anchor
                size="sm"
                href={/^https?:\/\//.test(e.id) ? e.id : undefined}
                target="_blank"
                rel="noreferrer"
              >
                Manufacturer source
              </Anchor>
            ) : (
              <Text size="sm" c="dimmed">
                {e.kind === 'observation'
                  ? 'Collector observation'
                  : 'Stored source'}{' '}
                · {e.id}
              </Text>
            )}
            {quotes && e.quote && (
              <Text
                component="blockquote"
                size="sm"
                className="project-source-quote"
              >
                “{e.quote}”
              </Text>
            )}
          </div>
        );
      })}
    </Stack>
  );
}

export function PreviewDocument({
  document,
}: {
  document: ProjectDetail['documents'][number];
}) {
  const viewer = useContext(EvidenceContext);
  return (
    <Button
      size="xs"
      variant="light"
      onClick={() =>
        viewer?.open({ kind: 'document', id: document.id, page: 1, quote: '' })
      }
    >
      View original
    </Button>
  );
}
